import React, { useEffect, useState } from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Send } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, EASE } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { useAssets, tourCover } from "@/lib/assets";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function Apply() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const assets = useAssets();
  useSeo({ title: t("seo.apply.title"), description: t("seo.apply.desc"), path: "/candidatura" });
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({ tour_slug: params.get("tour") || "", name: "", email: "", phone: "", consent: false });
  const [answers, setAnswers] = useState({});
  const [fields, setFields] = useState([]);
  const [tourInfo, setTourInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/tours").then((r) => {
      setTours(r.data);
      if (!params.get("tour") && r.data.length > 0) {
        setForm((f) => ({ ...f, tour_slug: r.data[0].slug }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.tour_slug) return;
    setAnswers({});
    api.get(`/tours/${form.tour_slug}/form`, { params: { lang } }).then((r) => {
      setFields(r.data.form_fields || []);
      setTourInfo(r.data);
    }).catch(() => {
      setFields([]);
      setTourInfo(null);
    });
  }, [form.tour_slug, lang]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setA = (id) => (e) => setAnswers((a) => ({ ...a, [id]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/applications", {
        tour_slug: form.tour_slug,
        name: form.name,
        email: form.email,
        phone: form.phone,
        consent: form.consent,
        answers: Object.entries(answers).map(([field_id, value]) => ({ field_id, value })),
      });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const selectedTour = tours.find((x) => x.slug === form.tour_slug);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />

      <section className="pt-32 sm:pt-44 pb-16" data-testid="apply-page">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow mb-3">{t("apply.eyebrow")}</p>
                <h1 className="font-serif font-semibold text-4xl sm:text-6xl leading-[1.0]">{t("apply.title")}</h1>
              </Reveal>
              <Reveal delay={0.15}>
                <ul className="mt-10 space-y-4">
                  {[t("apply.step1"), t("apply.step2"), t("apply.step3")].map((s, i) => (
                    <li key={i} className="flex gap-4 items-start">
                      <span className="font-mono text-copper text-sm pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-cream2 text-sm leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
              {selectedTour && (
                <Reveal delay={0.25}>
                  <div className="mt-10 relative border border-copper/20 overflow-hidden max-w-sm sm:max-w-md lg:max-w-none">
                    <img
                      src={imgSrc(tourCover(assets, selectedTour), 800)}
                      srcSet={imgSet(tourCover(assets, selectedTour), [400, 800, 1200])}
                      sizes="(max-width: 1024px) 100vw, 33vw"
                      alt={selectedTour.title}
                      className="w-full aspect-[16/10] object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 grain pointer-events-none" />
                    <div className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-cream2">{selectedTour.dates_label}</div>
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                {sent ? (
                  <motion.div
                    className="card-dark p-10 text-center"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    data-testid="apply-success"
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-copper text-copper mb-6"><Check className="h-7 w-7" /></span>
                    <h2 className="font-serif text-3xl text-cream">{t("apply.successTitle")}</h2>
                    <p className="mt-3 text-cream2">{t("apply.successText")}</p>
                    <button
                      className="btn-ghost mt-8"
                      data-testid="apply-again-btn"
                      onClick={() => {
                        setSent(false);
                        setForm({ tour_slug: form.tour_slug, name: "", email: "", phone: "", consent: false });
                        setAnswers({});
                      }}
                    >
                      {t("apply.again")}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="card-dark p-6 sm:p-10 space-y-5" data-testid="apply-form">
                    <div>
                      <label className="label" htmlFor="ap-tour">{t("apply.tour")} *</label>
                      <select id="ap-tour" className="field" required value={form.tour_slug} onChange={setF("tour_slug")} data-testid="apply-tour-select">
                        {tours.map((tour) => (
                          <option key={tour.id} value={tour.slug}>{tour.title}</option>
                        ))}
                      </select>
                      {tourInfo && <p className="font-mono text-[10px] text-sand mt-2 uppercase tracking-widest">{tourInfo.location}, {tourInfo.country} · {tourInfo.dates_label}</p>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="label" htmlFor="ap-name">{t("contact.name")}</label>
                        <input id="ap-name" required className="field" value={form.name} onChange={setF("name")} data-testid="apply-name-input" />
                      </div>
                      <div>
                        <label className="label" htmlFor="ap-email">{t("contact.email")}</label>
                        <input id="ap-email" type="email" required className="field" value={form.email} onChange={setF("email")} data-testid="apply-email-input" />
                      </div>
                    </div>
                    <div>
                      <label className="label" htmlFor="ap-phone">{t("contact.phone")}</label>
                      <input id="ap-phone" className="field" value={form.phone} onChange={setF("phone")} data-testid="apply-phone-input" />
                    </div>

                    {fields.length > 0 && (
                      <div className="pt-2 border-t border-white/5">
                        <p className="eyebrow mt-4 mb-5">{t("apply.fieldsTitle")}</p>
                        <div className="space-y-5">
                          {fields.map((f) => (
                            <div key={f.id}>
                              {f.type !== "checkbox" && (
                                <label className="label" htmlFor={`ap-${f.id}`}>{f.label}{f.required ? " *" : ""}</label>
                              )}
                              {f.type === "textarea" ? (
                                <textarea id={`ap-${f.id}`} rows="3" className="field resize-none" required={f.required} value={answers[f.id] || ""} onChange={setA(f.id)} data-testid={`apply-field-${f.id}`} />
                              ) : f.type === "select" ? (
                                <select id={`ap-${f.id}`} className="field" required={f.required} value={answers[f.id] || ""} onChange={setA(f.id)} data-testid={`apply-field-${f.id}`}>
                                  <option value="">—</option>
                                  {(f.options || []).map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                  ))}
                                </select>
                              ) : f.type === "checkbox" ? (
                                <label className="flex items-center gap-3 text-sm text-cream2 cursor-pointer">
                                  <input type="checkbox" className="accent-[#C86D3B] w-4 h-4" checked={answers[f.id] === "true"} onChange={(e) => setA(f.id)(e.target.checked ? "true" : "")} data-testid={`apply-field-${f.id}`} />
                                  {f.label}{f.required ? " *" : ""}
                                </label>
                              ) : (
                                <input id={`ap-${f.id}`} type={f.type === "number" ? "number" : "text"} className="field" required={f.required} value={answers[f.id] || ""} onChange={setA(f.id)} data-testid={`apply-field-${f.id}`} />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <label className="flex items-start gap-3 text-sm text-cream2 cursor-pointer pt-2">
                      <input type="checkbox" required checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="accent-[#C86D3B] w-4 h-4 mt-0.5" data-testid="apply-consent" />
                      <span>
                        {t("apply.consent")} — {t("consent.pre")}{" "}
                        <Link to="/privacy" className="link-underline text-copperlight" onClick={(e) => e.stopPropagation()} data-testid="apply-privacy-link">{t("consent.link")}</Link>.
                      </span>
                    </label>

                    {error && <p className="text-sm text-red-400" data-testid="apply-error">{error}</p>}
                    <button type="submit" disabled={sending} className="btn-copper w-full sm:w-auto disabled:opacity-50" data-testid="apply-submit-btn">
                      <Send className="h-4 w-4" /> {sending ? "…" : t("apply.submit")}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
